import styled from '@emotion/styled';
import { captureException } from '@sentry/minimal';
import { Action } from 'history';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router';
import { Redirect, Route, Switch, useHistory, useParams, useRouteMatch } from 'react-router-dom';

import { AudioPage } from 'src/modules/newPrograms/components/AudioPage';
import { MarkdownPage } from 'src/modules/newPrograms/components/MarkdownPage';
import { ProgressBar } from 'src/modules/newPrograms/components/ProgressBar';
import { ProgramRatingPage } from 'src/modules/newPrograms/components/ProgramRatingPage';
import { SelectPage } from 'src/modules/newPrograms/components/SelectPage';
import { TextInputPage } from 'src/modules/newPrograms/components/TextInputPage';
import { VideoPage } from 'src/modules/newPrograms/components/VideoPage';
import { WelcomePage } from 'src/modules/newPrograms/components/WelcomePage';
import { ProgramWrapper, useProgramContext } from 'src/modules/newPrograms/contexts/programContext';
import {
    ProgramFragment,
    ProgramPageFragment,
    useGetProgramWithPagesBySlugQuery,
    useGetProgramWithPagesQuery,
} from 'src/modules/newPrograms/documents/getProgram.generated';
import { NewPageTypes, PageConversion } from 'src/modules/newPrograms/models/program';
import closeIcon from 'src/public/img/close-icon.svg';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import { color, layout } from 'src/shared/styles/variables';

import { EarlyExitFeedbackModal } from '../components/EarlyExitFeedbackModal';
import { PageBackground } from '../components/PageBackground';
import type { PageNavigationComponentProps } from '../components/Page';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { colors as dsColors } from '@modernhealth/vital-foundation';
import { ProgramStateTestID } from 'src/modules/newPrograms/pages/testIds';

const Container = styled.div({
    padding: '0px 1.5em',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    width: '100%',
    alignSelf: 'center',
    position: 'relative',
});

const ContentContainer = styled.div({
    maxWidth: layout.containerWidth,
    width: '100%',
    margin: '1.75rem',
    alignSelf: 'center',
    justifySelf: 'flex-start',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
});

const ExitContainer = styled.button({
    height: 32,
    width: 32,
    border: `2px solid ${color.catskillWhite}`,
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'flex-end',
    marginTop: 12,
    cursor: 'pointer',
    img: {
        width: 8,
    },
    '&&:focus-visible': {
        boxShadow: `0px 0px 0px 2px ${dsColors.cyan20}`,
    },
    backgroundColor: dsColors.white,
});

interface Props {
    program: ProgramFragment;
    page: ProgramPageFragment;
    navigateToNext: () => void;
    navigateToPrevious: () => void;
    /**
     * Component to render the program's navigation.
     * By default this renders Back/Forward buttons.
     */
    navigationComponent?: React.FC<PageNavigationComponentProps>;
}

export const ProgramPage: React.FC<Props> = ({
    page,
    program,
    navigateToNext,
    navigateToPrevious,
    navigationComponent,
}) => {
    const { isFirstPage, isLastPage, showEarlyExitFeedback } = useProgramContext();

    const history = useHistory();
    const [navigationAttemptDetails, setNavigationAttemptDetails] = useState<{
        pathname: string;
        action: Action;
    } | null>(null);

    // this should prevent users from navigating out of the program if early exit feedback is enabled
    // any navigate out attempt should result in showing early exit feedback modal
    // pressing on the skip button in the modal should take the user to where he initially wanted to go
    // we're keeping this information in the `navigationAttemptDetails`
    useEffect(() => {
        const unblock = history.block((location, action) => {
            if (!showEarlyExitFeedback) return;

            // block navigate out attempt and show early exit feedback modal
            if (!isLastPage && !navigationAttemptDetails) {
                setNavigationAttemptDetails({ pathname: location.pathname, action });

                return false;
            }

            // allow navigate event to go through
            return;
        });

        return () => {
            unblock();
        };
    }, [isLastPage, showEarlyExitFeedback, navigationAttemptDetails]);

    const handleEarlyExitFeedbackClose = () => {
        switch (navigationAttemptDetails?.action) {
            case 'PUSH':
                history.push(navigationAttemptDetails.pathname);
                break;
            case 'REPLACE':
                history.replace(navigationAttemptDetails.pathname);
                break;
            case 'POP':
                history.goBack();
                break;
            default:
                history.goBack();
        }
    };

    const commonProps = {
        onNavigatePrev: () => navigateToPrevious(),
        onNavigateNext: () => navigateToNext(),
        navigationComponent,
    };

    const type = PageConversion[page.type];
    const isAudioPage = type === NewPageTypes.Audio && page.audioFile;

    let ProgramPageComponent = <MarkdownPage page={page} {...commonProps} />;

    if (isFirstPage && !isLastPage) {
        ProgramPageComponent = <WelcomePage page={page} onClick={() => navigateToNext()} />;
    }

    if (isAudioPage) {
        ProgramPageComponent = <AudioPage page={page} {...commonProps} />;
    }

    if (type === NewPageTypes.MultiSelectQuestion) {
        ProgramPageComponent = <SelectPage page={page} {...commonProps} />;
    }

    if (type === NewPageTypes.SingleSelectQuestion) {
        ProgramPageComponent = <SelectPage page={page} {...commonProps} />;
    }

    if (type === NewPageTypes.TextInputQuestion) {
        ProgramPageComponent = <TextInputPage page={page} {...commonProps} />;
    }

    if (type === NewPageTypes.Video) {
        ProgramPageComponent = <VideoPage page={page} {...commonProps} />;
    }

    return (
        <>
            {ProgramPageComponent}
            <EarlyExitFeedbackModal
                visible={!!navigationAttemptDetails}
                onContinueProgram={() => setNavigationAttemptDetails(null)}
                onExit={handleEarlyExitFeedbackClose}
                programUuid={program.uuid}
            />
        </>
    );
};

const preloadImage = (src?: string) => {
    if (!src) {
        return;
    }

    const image = new Image();
    image.src = src;
};

interface ProgramStateProps {
    navigationComponent?: React.FC<PageNavigationComponentProps>;
    onFinish?: () => void;
}

const AnimationContainer = styled.div<{ direction: 'left' | 'right' }>(({ direction }) => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',

    '&.program-enter': {
        opacity: 0,
        transform: `translate(${direction === 'left' ? '50%' : '-50%'}, 0)`,
    },
    '&.program-enter-active': {
        opacity: 1,
        transition: 'transform 250ms ease-in, opacity 350ms ease-in',
        transform: 'translate(0,0)',
    },
    '&.program-exit': {
        transform: 'translate(0,0)',
    },
    '&.program-exit-active': {
        opacity: 0,
        transform: `translate(${direction === 'left' ? '-25%' : '25%'}, 0)`,
        transition: 'opacity 200ms ease-out, transform 250ms',
    },
}));

export const ProgramState: React.FC<ProgramStateProps> = ({ navigationComponent, onFinish }) => {
    const { t } = useTranslation();
    const [animationDirection, setAnimationDirection] = useState<'left' | 'right'>('left');
    const [pageNumber, setPageNumber] = useState(0);

    const {
        currentPage: page,
        completionPercent,
        program,
        nextPage,
        previousPage,
        onExit,
        isLastPage,
    } = useProgramContext();

    const pages = useMemo(() => program?.pages, [program]);
    const [programFinished, setProgramFinished] = useState(false);

    useEffect(() => {
        if (program) {
            document.title = t('programs:page.title', { programName: program.name });
        }

        return () => {
            document.title = t('shared:page.title');
        };
    }, [t, program]);

    useEffect(() => {
        if (isLastPage && !programFinished) {
            if (onFinish) {
                onFinish();
            }
            setProgramFinished(true);
        }
    }, [isLastPage, onFinish, programFinished]);

    useEffect(() => {
        const keysToPreload = ['firstBackgroundImageUrl', 'secondBackgroundImageUrl', 'image'];

        if (pages) {
            pages.forEach(page => keysToPreload.forEach(key => preloadImage(page?.[key])));
        }
    }, [pages]);

    if (!program || !page) {
        return <CenteredSpinner />;
    }
    const hideProgressBar = pages.length === 1;

    const handlePageChange = (direction: 'next' | 'previous') => {
        const isNext = direction === 'next';
        setAnimationDirection(isNext ? 'left' : 'right');
        setTimeout(() => {
            setPageNumber(page => (isNext ? page + 1 : page - 1));
        }, 100);
        setTimeout(() => {
            isNext ? nextPage() : previousPage();
        }, 300);
    };

    return (
        <Container data-testid={ProgramStateTestID.Container}>
            <ContentContainer data-testid={ProgramStateTestID.ContentContainer}>
                {!hideProgressBar && <ProgressBar progressPercentage={completionPercent} />}
                <ExitContainer onClick={onExit} data-testid={ProgramStateTestID.ExitButton}>
                    <img src={closeIcon} alt="exit from program icon" />
                </ExitContainer>
                <SwitchTransition>
                    <CSSTransition key={pageNumber} timeout={400} classNames="program">
                        <AnimationContainer direction={animationDirection}>
                            <ProgramPage
                                program={program}
                                page={page}
                                navigationComponent={navigationComponent}
                                navigateToNext={() => handlePageChange('next')}
                                navigateToPrevious={() => handlePageChange('previous')}
                            />
                        </AnimationContainer>
                    </CSSTransition>
                </SwitchTransition>
            </ContentContainer>
            <PageBackground
                testID={ProgramStateTestID.FirstBackground}
                backgroundImageUrl={page.firstBackgroundImageUrl ?? ''}
                backgroundImagePosition={page.firstBackgroundImagePosition}
            />
            <PageBackground
                testID={ProgramStateTestID.SecondBackground}
                backgroundImageUrl={page.secondBackgroundImageUrl ?? ''}
                backgroundImagePosition={page.secondBackgroundImagePosition}
            />
        </Container>
    );
};

interface ProgramProps extends ProgramStateProps {
    /**
     * Internal link to redirect to if the program fails to load.
     */
    redirectTo?: string;
}

interface ProgramByUuidProps extends ProgramProps {
    uuid: string;
    /**
     * Callback for when the user exits the program.
     * `finished` is `true` when the user has made it
     * to the last page.
     *
     * By default, the exit action does nothing. Implementors
     * must add navigation logic.
     */
    onExit: (finished: boolean) => void;
    onFinish?: () => void;
}

/**
 * Renders a program given a program uuid (not global ID)
 */
export const ProgramByUuid: React.FC<ProgramByUuidProps> = ({
    uuid,
    onExit,
    onFinish,
    navigationComponent,
    redirectTo = '/programs',
}) => {
    const { data, loading, error } = useGetProgramWithPagesQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            uuid,
        },
    });

    if (loading) {
        return <CenteredSpinner />;
    }

    if (error) {
        captureException(error);

        // TODO(evanmh): Consider error state.
        return <Redirect to={redirectTo} />;
    }

    if (!data?.program) {
        // TODO(evanmh): Consider not found state.
        return <Redirect to={redirectTo} />;
    }

    return (
        <ProgramWrapper showEarlyExitFeedback={false} program={data.program} onExit={onExit}>
            <ProgramState navigationComponent={navigationComponent} onFinish={onFinish} />
        </ProgramWrapper>
    );
};

interface ProgramBySlugProps extends ProgramProps {
    slug: string;
}

/**
 * Renders a program given a slug
 */
export const IndividualProgram: React.FC<ProgramBySlugProps> = ({
    slug,
    navigationComponent,
    redirectTo = '/programs',
}) => {
    const featureFlags = useFlags();
    const history = useHistory();
    const { path, url } = useRouteMatch();

    const { data, loading, error } = useGetProgramWithPagesBySlugQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            slug,
        },
    });

    if (loading) {
        return <CenteredSpinner />;
    }

    if (error) {
        captureException(error);

        // TODO(evanmh): Consider error state.
        return <Redirect to={redirectTo} />;
    }

    if (!data?.programBySlug) {
        // TODO(evanmh): Consider not found state.
        return <Redirect to={redirectTo} />;
    }

    const showEarlyExitFeedback = featureFlags.showProgramsEarlyExitFeedback ?? false;

    const showProgramRatings =
        (data.programBySlug.showRatings && featureFlags.showRateProgramPageWeb) ?? false;

    return (
        <ProgramWrapper
            program={data.programBySlug}
            showEarlyExitFeedback={showEarlyExitFeedback}
            onExit={finished => {
                if (finished && showProgramRatings) {
                    history.push(`${url}/feedback`);
                } else {
                    history.goBack();
                }
            }}
        >
            <Switch>
                <Route path={path} exact>
                    <ProgramState navigationComponent={navigationComponent} />
                </Route>
                <Route path={`${path}/feedback`} exact>
                    <ProgramRatingPage />
                </Route>
            </Switch>
        </ProgramWrapper>
    );
};

interface Params {
    slug: string;
}

/**
 * The page route component for an individual program
 *
 * Expects a route with the format `/:slug`
 */
export const Program: React.FC<RouteComponentProps<Params, {}, {}>> = () => {
    const { slug } = useParams<Params>();

    return <IndividualProgram slug={slug} />;
};
